import { Component, OnInit, OnDestroy, ChangeDetectorRef, signal, ElementRef, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { AppService } from '../../core/service/app.service';
import { HeaderService, HeaderConfig } from '../../core/service/header.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import { sanitizeHtmlContent } from 'src/app/shared/utils/sanitize-content.util';
import { IconContractSignatureComponent } from 'src/app/shared/icon/menu/icon-contract-signature';
import { IconPurchaseComponent } from 'src/app/shared/icon/purchase-master/icon-purchase';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuModule } from 'headlessui-angular';
import { FormsModule } from '@angular/forms';
import { NgClass, NgComponentOutlet, AsyncPipe } from '@angular/common';
import { Subject, takeUntil, filter } from 'rxjs';
import { SharedModule } from '../../shared/shared.module';
import { AuthService } from '../../auth/auth.service';
import { VendorService } from '../../pages/vendor/vendor.service';
import { TenantService } from '../../core/service/tenant.service';
import { TenantSwitchService } from '../../core/service/tenant-switch.service';
import { AlertService } from '../../core/service/alert.service';
import { NotificationMasterService } from 'src/app/pages/notification-master/service/notification-master.service';
import { HttpParams } from '@angular/common/http';
import { NotificationComponent, Notification } from '../notification/notification.component';
import { SocketService } from 'src/app/core/service/socket.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { ImageService } from '../../core/service/image.service';
import { ChatUnreadCountService } from 'src/app/pages/chat/service/chat-unread-count.service';
import { VendorChatService } from 'src/app/pages/chat/service/vendor-chat.service';
import { VendorChatSocketService } from 'src/app/pages/chat/service/vendor-chat-socket.service';
import { CurrencyService } from '../../core/service/currency.service';
import { VendorCounterOfferEventNameService } from '../../core/service/vendor-counter-offer-event-name.service';
import { TenantPermissionService } from '../../core/service/tenant-permission.service';
import { QuotationUnsavedChangesService } from '../../core/service/quotation-unsaved-changes.service';
import { TimezoneNavigationService } from '../../core/service/timezone-navigation.service';
import { VendorHeaderTimezoneComponent } from '../../shared/vendor-header-timezone/vendor-header-timezone.component';
import {
    enrichCounterOfferNotificationMessage,
    extractNotificationEventId,
    extractNotificationEventName,
    isCounterOfferNotificationPayload,
} from 'src/app/shared/utils/notification-alert.helpers';

@Component({
    selector: 'header',
    templateUrl: './header.html',
    animations: [
        trigger('toggleAnimation', [
            transition(':enter', [style({ opacity: 0, transform: 'scale(0.95)' }), animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))]),
            transition(':leave', [animate('75ms', style({ opacity: 0, transform: 'scale(0.95)' }))]),
        ]),
    ],
    standalone: true,
    imports: [NgClass, RouterLink, FormsModule, MenuModule, RouterLinkActive, TranslateModule, SharedModule, AsyncPipe, NotificationComponent, VendorHeaderTimezoneComponent],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    /** De-duplicate rapid socket notifications that arrive multiple times. */
    private readonly recentNotificationKeys = new Map<string, number>();
    store: any;
    search = false;
    headerConfig$ = this.headerService.headerConfig$;
    isDev = !environment.production;
    @ViewChild('notifMenuButton') notifMenuButton!: ElementRef<HTMLButtonElement>;
    showActivityMenu = signal(true);
    // Tenant management
    tenants: any[] = [];
    selectedTenant: any;
    tenantOptions: any[] = [];
    selectedTenantOption: any = null;
    vendorData: any = {};
    vendorLoginData: any;
    profile_picture: string = '';
    VendorName: string = '';
    tenantCurrency: string = "";
    tenantCurrencySymbol: string = "";
    // Notifications data for dropdown
    // Using Angular components for icons (rendered via NgComponentOutlet)
    notifications!: Array<Notification>;
    totalChatUnreadCount: number = 0;
    private chatUnreadRefreshTimer?: ReturnType<typeof setTimeout>;
    userInfo = signal<any>({});

    // Read/Unread filter state
    // selectedNotifTab: 'read' | 'unread' | 'all' = 'all';
    selectedNotifTab = signal<string>('all');
    currencies: { code: string; symbol: string }[] = [];
    /** True after user picks a currency from the header menu (not on tenant init). */
    currencyDropdownSelected = false;
    get filteredNotifications() {
        if (this.selectedNotifTab() === 'read') return this.notifications.filter((n) => n.read);
        if (this.selectedNotifTab() === 'unread') return this.notifications.filter((n) => !n.read);
        if (this.selectedNotifTab() === 'all') return this.notifications;
        return [];
    }

    /** Bell indicator: only when at least one notification is unread (same sense as Unread tab / `read` on each item). */
    get hasUnreadNotifications(): boolean {
        return (this.notifications ?? []).some((n) => !n.read);
    }

    /** Multi-tenant switcher is only for super vendors with more than one tenant access. */
    get isSuperVendor(): boolean {
        const role =
            this.userInfo()?.vendor_role ??
            this.vendorData?.vendor_role ??
            this.authService.getVendorData()?.vendor_role ??
            this.readVendorRoleFromAccessToken();
        return role === 'SUPER_VENDOR';
    }

    private readVendorRoleFromAccessToken(): string | null {
        try {
            const token = this.authService.getToken();
            if (!token) {
                return null;
            }
            const payloadSegment = token.split('.')[1];
            if (!payloadSegment) {
                return null;
            }
            const payload = JSON.parse(atob(payloadSegment.replace(/-/g, '+').replace(/_/g, '/')));
            return typeof payload.vendor_role === 'string' ? payload.vendor_role : null;
        } catch {
            return null;
        }
    }

    get showTenantSwitcher(): boolean {
        return this.isSuperVendor && this.tenantOptions.length > 0;
    }
    messages = [
        {
            id: 1,
            image: sanitizeHtmlContent(
                this.sanitizer,
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`
            ),
            title: 'Congratulations!',
            message: 'Your OS has been updated.',
            time: '1hr',
        },
        {
            id: 2,
            image: sanitizeHtmlContent(
                this.sanitizer,
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`
            ),
            title: 'Did you know?',
            message: 'You can switch between artboards.',
            time: '2hr',
        },
        {
            id: 3,
            image: sanitizeHtmlContent(
                this.sanitizer,
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`
            ),
            title: 'Something went wrong!',
            message: 'Send Reposrt',
            time: '2days',
        },
        {
            id: 4,
            image: sanitizeHtmlContent(
                this.sanitizer,
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`
            ),
            title: 'Warning',
            message: 'Your password strength is low.',
            time: '5days',
        },
    ];

    constructor(
        public readonly translate: TranslateService,
        public readonly storeData: Store<any>,
        public readonly router: Router,
        private readonly appSetting: AppService,
        private readonly sanitizer: DomSanitizer,
        public readonly headerService: HeaderService,
        public readonly authService: AuthService,
        private readonly vendorService: VendorService,
        private readonly tenantService: TenantService,
        private readonly tenantSwitchService: TenantSwitchService,
        private readonly alertService: AlertService,
        private readonly cdr: ChangeDetectorRef,
        private readonly notificationService: NotificationMasterService,
        public readonly imageService: ImageService,
        private readonly socketService: SocketService,
        private readonly toastService: ToastrService,
        private readonly chatUnreadCountService: ChatUnreadCountService,
        private readonly vendorChatService: VendorChatService,
        private readonly vendorChatSocketService: VendorChatSocketService,
        public readonly currencyService: CurrencyService,
        private readonly counterOfferEventNameService: VendorCounterOfferEventNameService,
        public readonly tenantPermissionService: TenantPermissionService,
        private readonly quotationUnsavedChangesService: QuotationUnsavedChangesService,
        private readonly timezoneNavigationService: TimezoneNavigationService,
    ) {
        // Build notifications using the icon components
        this.notifications = [];
    }
    initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }

    ngOnInit() {
       
        this.initStore();
        this.userInfo.set(this.authService.getVendorData());
        this.bootstrapTenantsFromStoredVendorData();
        const tenantCurrency =
            this.userInfo()?.vendor_tenant_accesses?.[0]?.tenant_info?.currency;
        const normalizedTenant = tenantCurrency?.trim().toUpperCase();
        this.tenantCurrency = normalizedTenant;
        this.tenantCurrencySymbol = this.getCurrencySymbol(normalizedTenant);
        const tenantSymbol = this.getCurrencySymbol(normalizedTenant);
        this.currencyService.initializeFromTenant(normalizedTenant, tenantSymbol);
        this.currencyDropdownSelected = false;
        this.currencies = [
            { code: 'INR', symbol: '₹' },
            { code: 'USD', symbol: '$' },
        ];
        if (normalizedTenant) {
            const existingIndex = this.currencies.findIndex(
              (currency) => currency.code.toUpperCase() === normalizedTenant,
            );
            if (existingIndex >= 0) {
              this.currencies.splice(existingIndex, 1);
            }
        }
        this.initializeTenantFromStorage();
        this.setupSubscriptions();
        this.fetchNotifications();
        this.initializeComponent();
        this.loadHeaderChatUnreadCount();
        this.chatUnreadCountService
            .getTotalUnreadCount()
            .pipe(takeUntil(this.destroy$))
            .subscribe((count) => {
                this.totalChatUnreadCount = count;
                this.cdr.detectChanges();
            });

        // Real-time updates for header chat dot
        this.vendorChatSocketService
            .onChatMessage()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.scheduleHeaderChatUnreadRefresh();
            });

        this.vendorChatSocketService
            .onMessagesRead()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.scheduleHeaderChatUnreadRefresh();
            });

        this.subscribeToWebSocketNotifications();
        this.setActiveDropdown();
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.setActiveDropdown();
            }
        });
        this.getTenantPermissions();
    }
    getTenantPermissions(): void {
        this.tenantPermissionService
            .loadTenantPermissions()
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }
    getCurrencySymbol(currencyCode: string): string {
        const parts = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: currencyCode,
        }).formatToParts(0);
      
        return (
          parts.find(part => part.type === 'currency')?.value ??
          currencyCode
        );
      }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.chatUnreadRefreshTimer) {
            clearTimeout(this.chatUnreadRefreshTimer);
        }
    }

    private loadHeaderChatUnreadCount(): void {
        const params = new HttpParams().set('page', '1').set('page_size', '50');
        this.vendorChatService
            .getConversations(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response?.status && response?.data?.conversations) {
                        const totalUnread = this.chatUnreadCountService.calculateTotalUnreadCount(response.data.conversations);
                        this.chatUnreadCountService.updateTotalUnreadCount(totalUnread);
                    } else {
                        this.chatUnreadCountService.updateTotalUnreadCount(0);
                    }
                },
                error: () => {
                    this.chatUnreadCountService.updateTotalUnreadCount(0);
                },
            });
    }

    private scheduleHeaderChatUnreadRefresh(): void {
        // Debounce bursty socket events
        if (this.chatUnreadRefreshTimer) {
            clearTimeout(this.chatUnreadRefreshTimer);
        }
        this.chatUnreadRefreshTimer = setTimeout(() => {
            this.loadHeaderChatUnreadCount();
        }, 600);
    }

    /**
     * Initialize tenant from localStorage on component load
     */
    private initializeTenantFromStorage(): void {
        const storedTenantId = localStorage.getItem('currentTenantId') || '';
        if (storedTenantId && storedTenantId !== 'undefined' && storedTenantId !== 'select_tenant') {
            this.selectedTenant = storedTenantId;
            // Will be set properly after tenants are loaded
        }
    }

    /** Profile API often omits tenant accesses; keep login payload when present. */
    private resolveVendorTenantAccesses(profilePayload: any): any[] {
        const fromProfile = profilePayload?.vendor_tenant_accesses;
        if (Array.isArray(fromProfile) && fromProfile.length > 0) {
            return fromProfile;
        }
        const fromStorage = this.authService.getVendorData()?.vendor_tenant_accesses;
        return Array.isArray(fromStorage) ? fromStorage : [];
    }

    /** Populate tenant dropdown from login payload before profile API returns. */
    private bootstrapTenantsFromStoredVendorData(): void {
        const vendor = this.authService.getVendorData();
        const accesses = this.resolveVendorTenantAccesses(vendor);
        if (!accesses.length) {
            return;
        }
        this.vendorData = vendor;
        this.accessTenantList(accesses);

        const storedTenantId = localStorage.getItem('currentTenantId') || '';
        if (this.isValidStoredTenant(storedTenantId)) {
            this.setActiveTenant(storedTenantId);
            return;
        }

        const firstSubdomain = this.tenants[0]?.tenant_subdomain;
        if (firstSubdomain) {
            this.setActiveTenant(firstSubdomain);
        }
    }

    /**
     * Initialize core component data
     */
    private initializeComponent(): void {
        this.getVendorById();
        this.vendorData = this.authService.getVendorData();
    }

    /**
     * Setup all subscriptions with proper cleanup
     */
    private setupSubscriptions(): void {
        // Profile picture subscription
        this.authService.profilePicture$.pipe(takeUntil(this.destroy$)).subscribe((profilePicture) => {
            this.profile_picture = profilePicture;
            // Update vendorData to reflect profile picture change while preserving other data
            if (this.vendorData) {
                this.vendorData = {
                    ...this.vendorData,
                    profile_picture: profilePicture,
                };
            }
            this.cdr.detectChanges();
        });

        // Profile name subscription
        this.authService.profileName$.pipe(takeUntil(this.destroy$)).subscribe((profileName) => {
            const normalizedName = profileName?.company_name || profileName?.name || '';
            this.VendorName = normalizedName;
            // Update vendorData to reflect name change while preserving other data like email_id
            if (this.vendorData) {
                this.vendorData = {
                    ...this.vendorData,
                    name: normalizedName,
                    company_name: normalizedName,
                };
            }
            this.cdr.detectChanges();
        });

        // Tenant refresh subscription
        this.authService.tenantRefresh$.pipe(takeUntil(this.destroy$)).subscribe((shouldRefresh) => {
            if (shouldRefresh) {
                this.handleTenantRefresh();
            }
        });

        // Tenant service subscription
        this.tenantService.tenant$.pipe(takeUntil(this.destroy$)).subscribe((tenantId) => {
            if (tenantId !== this.selectedTenant) {
                this.selectedTenant = tenantId;
                this.selectedTenantOption = this.tenantOptions.find((option: any) => option.value === tenantId) || null;
            }
            if (!tenantId && this.tenants && this.tenants.length > 0) {
                const firstTenant = this.tenants[0];
                if (firstTenant?.tenant_subdomain) {
                    setTimeout(() => {
                        this.tenantService.setCurrentTenant(firstTenant.tenant_subdomain);
                    }, 100);
                }
            }
            this.cdr.detectChanges();
        });
    }

    /**
     * Subscribe to WebSocket in-app notifications (same `in_app_notification` event as admin app).
     */
    private subscribeToWebSocketNotifications(): void {
        this.socketService.socketNotificationData
            .pipe(
                takeUntil(this.destroy$),
                filter((data: any) => !!data && (!!data.notification || !!(data.title && data.message)))
            )
            .subscribe((data: any) => {
                this.handleIncomingNotification(data);
            });
    }

    /**
     * Handle payload from `in_app_notification` — mirrors admin header behavior.
     */
    private handleIncomingNotification(data: any): void {
        const notification = data.notification ?? data;
        const meta = notification.metadata ?? notification.meta_data ?? notification.meta ?? {};
        const baseMessage = notification.message ?? '';
        const isCounterOffer = isCounterOfferNotificationPayload(data, notification);
        const resolveEventName = (): string => {
            const fromPayload = extractNotificationEventName(data, notification, meta);
            if (fromPayload) {
                return fromPayload;
            }
            const remembered = this.counterOfferEventNameService.lookup(
                extractNotificationEventId(data, notification, meta)
            );
            if (remembered) {
                return remembered;
            }
            return this.counterOfferEventNameService.lookupRecent();
        };

        let message = baseMessage;
        if (isCounterOffer) {
            const eventName = resolveEventName();
            if (eventName) {
                this.counterOfferEventNameService.remember(extractNotificationEventId(data, notification, meta), eventName);
            }
            message = enrichCounterOfferNotificationMessage(baseMessage, eventName);
        }

        const mappedNotification: Notification = {
            id: notification.id ?? notification.notification_id ?? Date.now(),
            alert_type: notification.alertType ?? notification.alert_type ?? this.mapNotificationType(data.type),
            title: notification.title ?? '',
            message,
            meta,
            meta_data: meta,
            read: notification.isRead ?? notification.is_read ?? false,
            iconCmp: IconPurchaseComponent,
            time: notification.time ?? new Date().toLocaleString(),
            created_at: notification.createdAt ?? notification.created_at ?? new Date().toISOString(),
            action: notification.action,
        };

        const dedupeKey = this.getNotificationDedupeKey(mappedNotification);
        if (this.isRecentDuplicateNotification(dedupeKey)) {
            return;
        }

        const dup = this.notifications?.some((n) => n.id === mappedNotification.id || this.getNotificationDedupeKey(n) === dedupeKey);
        if (!dup) {
            this.notifications = [mappedNotification, ...(this.notifications || [])];
        }

        const title = mappedNotification.title || 'Notification';

        if (!isCounterOffer && !this.shouldSkipBidQuoteInAppToast(data, notification, title)) {
            const needsDeferredEventName =
                !resolveEventName() && /\bfor\s+event\s*\.?$/i.test(String(baseMessage).trim());

            if (needsDeferredEventName) {
                setTimeout(() => {
                    const deferredMessage = enrichCounterOfferNotificationMessage(baseMessage, resolveEventName());
                    this.toastService.info(deferredMessage, title);
                }, 200);
            } else {
                this.toastService.info(mappedNotification.message || '', title);
            }
        }
        this.cdr.detectChanges();
    }

    /**
     * `bidUpdate` / `quotationUpdate` toasts (with currency + part detail) are shown by VendorBidSocketToastService.
     * Skip the generic `in_app_notification` toast to avoid duplicates (same pattern as admin header).
     */
    private shouldSkipBidQuoteInAppToast(data: any, notification: any, title: string): boolean {
        const normalizedTitle = String(title ?? '').trim().toLowerCase();
        if (
            normalizedTitle === 'bid accepted' ||
            normalizedTitle === 'bid rejected' ||
            normalizedTitle === 'complete bid rejected' ||
            normalizedTitle === 'quote accepted' ||
            normalizedTitle === 'quote rejected'
        ) {
            return true;
        }

        const typeCandidates = [
            data?.type,
            notification?.type,
            notification?.alertType,
            notification?.alert_type,
            notification?.metadata?.type,
            notification?.meta_data?.type,
            notification?.meta?.type,
        ]
            .filter((v) => v != null && String(v).trim() !== '')
            .map((v) => String(v).trim().toUpperCase());

        const skipTypes = new Set([
            'BID_ACCEPTED',
            'BID_REJECTED',
            'QUOTATION_ACCEPTED',
            'QUOTATION_REJECTED',
        ]);

        return typeCandidates.some((t) => skipTypes.has(t));
    }

    private getNotificationDedupeKey(notification: Notification): string {
        const stableId = notification.id && Number(notification.id) > 0 ? String(notification.id) : '';
        if (stableId) {
            return `id:${stableId}`;
        }
        const createdAt = notification.created_at ? String(notification.created_at) : '';
        return `body:${notification.alert_type || ''}|${notification.title || ''}|${notification.message || ''}|${createdAt}`;
    }

    private isRecentDuplicateNotification(key: string): boolean {
        const now = Date.now();
        const ttlMs = 15000;

        for (const [k, ts] of this.recentNotificationKeys.entries()) {
            if (now - ts > ttlMs) {
                this.recentNotificationKeys.delete(k);
            }
        }

        if (this.recentNotificationKeys.has(key)) {
            return true;
        }

        this.recentNotificationKeys.set(key, now);
        return false;
    }

    private mapNotificationType(type: string | undefined): string {
        if (!type) {
            return 'SYSTEM';
        }
        const typeMap: Record<string, string> = {
            BID_NOTIFICATION: 'BID',
            EVENT_NOTIFICATION: 'AUCTION',
            QUOTATION_NOTIFICATION: 'QUOTATION',
            COUNTER_OFFER_NOTIFICATION: 'COUNTER_OFFER',
            SYSTEM_NOTIFICATION: 'SYSTEM',
        };
        return typeMap[type] || 'SYSTEM';
    }

    /**
     * Handle tenant refresh with proper error handling
     */
    private handleTenantRefresh(): void {
        try {
            this.vendorData = this.authService.getVendorData();
            // Update profile picture and name from vendorData
            if (this.vendorData) {
                this.profile_picture = this.vendorData?.profile_picture || '';
                this.VendorName = this.vendorData?.name || '';
                this.authService.hydrateVendorProfilePicture(
                    this.vendorData?.profile_picture || '',
                    this.vendorData?.id
                );
            }
            const accesses = this.vendorData?.vendor_tenant_accesses || [];
            this.tenants = this.accessTenantList(accesses);

            if (this.tenants.length > 0) {
                this.authService.setTenantInfo(this.tenants[0]);

                const currentTenant = this.tenants.find((tenant: any) => tenant?.tenant_subdomain === this.authService.getTenanatDataValue()?.tenant_subdomain);

                if (currentTenant) {
                    localStorage.setItem('currentTenantId', currentTenant.tenant_subdomain);
                    this.selectedTenant = currentTenant.tenant_subdomain;
                    this.selectedTenantOption = this.tenantOptions.find((option: any) => option.value === currentTenant.tenant_subdomain) || null;
                    this.tenantService.tenantSubject.next(currentTenant.tenant_subdomain);
                }
            }

            this.cdr.detectChanges();
        } catch (error) {
            this.handleError(error, 'tenant refresh');
        }
    }

    /**
     * Fetch vendor profile with proper error handling
     */
    private getVendorById(): void {
        this.vendorService
            .fetchProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    try {
                        const failed = res?.status === false;
                        const data = failed ? null : res?.data ?? res;
                        if (data?.id != null) {
                            this.processVendorData(data);
                        }
                    } catch (error) {
                        this.handleError(error, 'processing vendor data');
                    }
                },
                error: (error) => {
                    this.handleError(error, 'fetching vendor profile');
                },
            });
    }

    /**
     * Process vendor data from API response
     */
    private processVendorData(data: any): void {
        this.vendorLoginData = data;
        this.profile_picture = data?.profile_picture || '';
        this.VendorName = data?.company_name || data?.name || '';

        this.authService.applyVendorProfilePayload(data);
        this.vendorData = {
            ...(this.authService.getVendorData() || {}),
            email_id: data?.email_id || this.vendorData?.email_id || '',
            vendor_role: data?.vendor_role ?? this.vendorData?.vendor_role,
        };
        this.userInfo.set(this.authService.getVendorData());

        const accesses = this.resolveVendorTenantAccesses(data);
        this.tenants = this.accessTenantList(accesses);

        if (this.tenants.length > 0) {
            const existingTenantData = this.authService.getTenanatDataValue();
            this.authService.setTenantInfo(existingTenantData || this.tenants[0]);
        }

        setTimeout(() => {
            this.initializeTenantsAfterDataLoad();
            this.cdr.markForCheck();
        }, 100);
    }

    /**
     * Initialize tenant selection after vendor data is loaded
     */
    private initializeTenantsAfterDataLoad(): void {
        const storedTenantId = localStorage.getItem('currentTenantId') || '';
        if (this.isValidStoredTenant(storedTenantId)) {
            this.setActiveTenant(storedTenantId);
            return;
        }

        if (this.tenants && this.tenants.length > 0) {
            const firstTenant = this.tenants[0];
            this.setActiveTenant(firstTenant.tenant_subdomain);
            return;
        }

        this.selectedTenant = '';
        this.selectedTenantOption = null;
    }

    /**
     * Get selected tenant name for display
     */
    getSelectedTenantName(): string {
        if (this.selectedTenantOption) {
            return this.selectedTenantOption.label || this.selectedTenantOption.value || '';
        }
        const tenant = this.tenants.find((t: any) => t.tenant_subdomain === this.selectedTenant);
        return tenant?.tenant_name || 'Select Tenant';
    }

    /**
     * Check if the stored tenant ID is valid
     */
    private isValidStoredTenant(storedTenantId: string): boolean {
        if (!storedTenantId || storedTenantId === 'undefined' || storedTenantId === 'select_tenant') {
            return false;
        }

        return this.tenants.some((tenant: any) => tenant.tenant_subdomain === storedTenantId);
    }

    /**
     * Set the active tenant
     */
    private setActiveTenant(tenantId: string): void {
        const tenantData = this.tenants.find((tenant: any) => tenant.tenant_subdomain === tenantId);
        if (tenantData) {
            this.authService.setTenantInfo(tenantData);
            localStorage.setItem('currentTenantId', tenantId);
        }
        this.selectedTenant = tenantId;
        this.selectedTenantOption = this.tenantOptions.find((option: any) => option.value === tenantId) || null;
        this.tenantService.setCurrentTenant(tenantId);
        this.cdr.detectChanges();
    }

    accessTenantList(accesses: any) {
        this.tenants = accesses
            .filter((item: any) => item?.tenant_info?.tenant_subdomain)
            .map((item: any) => ({
                id: item.tenant_info.id,
                tenant_name: item.tenant_info.tenant_name,
                tenant_subdomain: item.tenant_info.tenant_subdomain,
                email: item.tenant_info.email,
                logo: item.tenant_info.logo,
            }));

        // Map tenants to dropdown options format
        this.tenantOptions = this.tenants.map((tenant: any) => ({
            value: tenant.tenant_subdomain,
            label: tenant.tenant_name,
        }));

        // Update selected option if tenant is already selected
        if (this.selectedTenant) {
            this.selectedTenantOption = this.tenantOptions.find((option: any) => option.value === this.selectedTenant) || null;
        }

        this.cdr.markForCheck();
        return this.tenants;
    }

    /**
     * Handle tenant change with improved error handling
     */
    async onTenantChange(selectedOption: any): Promise<void> {
        const selectedValue = typeof selectedOption === 'object' ? selectedOption.value : selectedOption;

        // Prevent multiple simultaneous switches
        if (this.tenantSwitchService.isSwitching()) {
            return;
        }

        try {
            const result = await this.tenantSwitchService.safeSwitchTenant(
                selectedValue,
                this.tenants,
                true // Navigate automatically for manual switches
            );

            if (!result.success) {
                // Reset to previous value on failure
                const currentTenantId = localStorage.getItem('currentTenantId') || '';
                this.selectedTenant = currentTenantId;
                this.selectedTenantOption = this.tenantOptions.find((option: any) => option.value === currentTenantId) || null;
            } else {
                // Update UI state after successful switch
                this.selectedTenant = selectedValue;
                this.selectedTenantOption = selectedOption;
                this.tenantPermissionService.reload().pipe(takeUntil(this.destroy$)).subscribe();
                this.cdr.detectChanges();
            }
        } catch (error) {
            this.handleError(error, 'tenant change');

            // Reset to previous value on error
            const currentTenantId = localStorage.getItem('currentTenantId') || '';
            this.selectedTenant = currentTenantId;
            this.selectedTenantOption = this.tenantOptions.find((option: any) => option.value === currentTenantId) || null;
        }
    }

    /**
     * Handle error logging and user notification
     */
    private handleError(error: any, context: string): void {
        if (this.alertService) {
            this.alertService.showAlert('Error', `Failed to ${context}. Please try again.`, 'error');
        }
    }

    setActiveDropdown() {
        const selector = document.querySelector('ul.horizontal-menu a[routerLink="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const all: any = document.querySelectorAll('ul.horizontal-menu .nav-link.active');
            for (let i = 0; i < all.length; i++) {
                all[0]?.classList.remove('active');
            }
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link');
                if (ele) {
                    ele = ele[0];
                    setTimeout(() => {
                        ele?.classList.add('active');
                    });
                }
            }
        }
    }

    removeNotification(value: number) {
        this.notifications = this.notifications.filter((d) => d.id !== value);
    }

    removeMessage(value: number) {
        this.messages = this.messages.filter((d) => d.id !== value);
    }

    changeLanguage(item: any) {
        this.translate.use(item.code);
        this.appSetting.toggleLanguage(item);
        if (this.store.locale?.toLowerCase() === 'ae') {
            this.storeData.dispatch({ type: 'toggleRTL', payload: 'rtl' });
        } else {
            this.storeData.dispatch({ type: 'toggleRTL', payload: 'ltr' });
        }
        window.location.reload();
    }

    goBack() {
        console.log('goBack');
    }

    /**Notifications */
    notificationType(type: string) {
        switch (type) {
            case 'read':
            case 'unread':
            case 'all':
                this.selectedNotifTab.set(type);
                this.fetchNotifications();
                break;
            default:
                this.fetchNotifications();
        }
    }

    getParams(): HttpParams {
        let params = new HttpParams().append('filter', this.selectedNotifTab());

        return params;
    }

    fetchNotifications() {
        this.showActivityMenu.set(true);
        this.notificationService.fetchNotifications(this.getParams()).subscribe({
            next: (res: any) => {
                if (res?.status) {
                    this.notifications = res?.data?.vendorAlerts.map((notification: any) => ({
                        id: notification?.id,
                        alert_type: notification?.alert_type,
                        title: notification?.title,
                        meta: notification?.meta_data,
                        read: notification?.is_read,
                        message: notification?.message,
                        iconCmp: IconPurchaseComponent,
                        created_at: notification?.created_at,
                    }));
                }
            },
            error: (error: any) => {
                this.alertService.showAlert('Error', error.error.messagee || error.error.error, 'error');
            },
        });
    }

    closeNotificationPopup() {
        this.showActivityMenu.set(false);
    }

    navigateToNotificationList() {
        this.router.navigate(['/notification-master/notification']);
    }

    async signOut() {
        const canLeave = await this.quotationUnsavedChangesService.confirmLeaveIfNeeded();
        if (!canLeave) {
            return;
        }
        this.authService.logout();
    }

    /** Tenant currency configuration — delegates to global CurrencyService for FX + live updates. */
    changeCurrency(symbol: string, code: string): void {
        this.currencyDropdownSelected = true;
        console.log('changeCurrency', symbol, code);
        this.currencyService.setSelectedCurrency(code, symbol);
        this.cdr.markForCheck();
    }

    clearCurrencySelection(): void {
        this.currencyDropdownSelected = false;
        this.currencyService.clearCurrencySelection();
        this.cdr.markForCheck();
    }

    showCurrencyPlaceholder(): boolean {
        return !this.currencyDropdownSelected;
    }

    currencyCode(): string {
        return this.currencyService.selectedCurrency().code;
    }

    currencySymbol(): string {
        return this.currencyService.selectedCurrency().symbol;
    }

    onNavLinkClick(event: MouseEvent, route: string): void {
        if (this.timezoneNavigationService.blockNavigationIfNeeded(route)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
}
